// Institutional · 3-card 3D dashboard stack
// (extracted from HeroArtwork.jsx; behaviour preserved)
import React from "react";
import { motion } from "framer-motion";
import { Wallet, TrendingUp, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { glassCard } from "./_shared";

function InstitutionalArt({ accent }) {
  const { t } = useTranslation();
  return (
    <div
      className="relative w-full aspect-square max-w-[460px] mx-auto overflow-hidden"
      data-testid="hero-art-institutional"
      style={{ perspective: "1400px" }}
    >
      {/* Backdrop halo · inset so the blur stays inside the artwork stage */}
      <div
        aria-hidden
        className="absolute inset-[12%] pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 50%, color-mix(in srgb, ${accent} 22%, transparent) 0%, transparent 65%)`,
          filter: "blur(10px)",
        }}
      />

      <div className="relative w-full h-full" style={{ transformStyle: "preserve-3d" }}>
        {/* CARD A · MINI CHART · sits in the back, larger */}
        <motion.div
          className="absolute rounded-2xl overflow-hidden"
          style={{
            ...glassCard(accent),
            left: "8%", top: "8%", width: "62%", aspectRatio: "16 / 11",
            transform: "rotateY(-12deg) rotateX(7deg) translateZ(-60px)",
          }}
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="px-3 py-2 border-b flex items-center justify-between font-mono uppercase tracking-wider"
               style={{ fontSize: 8.5, borderColor: "color-mix(in srgb, var(--brand) 18%, transparent)", color: "var(--text-3)" }}>
            <span className="inline-flex items-center gap-1.5">
              <span className="rounded-full" style={{ width: 5, height: 5, background: accent, boxShadow: `0 0 5px ${accent}` }} />
              XAU/USD · H1
            </span>
            <span style={{ color: accent }}>2,068.40</span>
          </div>
          <svg viewBox="0 0 200 90" className="w-full" style={{ height: "calc(100% - 26px)" }}>
            <defs>
              <linearGradient id="inst-fill-v4" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={accent} stopOpacity="0.4" />
                <stop offset="100%" stopColor={accent} stopOpacity="0" />
              </linearGradient>
            </defs>
            {[20, 40, 60].map((y) => (
              <line key={y} x1={6} y1={y} x2={194} y2={y}
                    stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" strokeDasharray="1.5 2" />
            ))}
            <motion.path
              d="M 6 70 L 22 65 L 38 60 L 54 58 L 70 50 L 86 48 L 102 42 L 118 38 L 134 30 L 150 28 L 166 20 L 182 14 L 194 12 L 194 86 L 6 86 Z"
              fill="url(#inst-fill-v4)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
            <motion.path
              d="M 6 70 L 22 65 L 38 60 L 54 58 L 70 50 L 86 48 L 102 42 L 118 38 L 134 30 L 150 28 L 166 20 L 182 14 L 194 12"
              fill="none" stroke={accent} strokeWidth="1.5" strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2.2, ease: "easeOut", repeat: Infinity, repeatType: "loop", repeatDelay: 2.2 }}
            />
            {/* Pulse marker at last point */}
            <motion.circle
              cx={194} cy={12} r={2}
              fill="none" stroke={accent} strokeWidth="0.5"
              initial={{ opacity: 0.85, scale: 1 }}
              animate={{ scale: [1, 3.5, 1], opacity: [0.85, 0, 0.85] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
            />
            <circle cx={194} cy={12} r={2} fill={accent} />
          </svg>
        </motion.div>

        {/* CARD B · ACCOUNT EQUITY · centre-right, prominent */}
        <motion.div
          className="absolute rounded-2xl p-4"
          style={{
            ...glassCard(accent, 94),
            right: "6%", top: "30%", width: "54%",
            transform: "rotateY(8deg) rotateX(-4deg) translateZ(20px)",
          }}
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={12} style={{ color: accent }} />
            <span className="font-mono uppercase tracking-[0.18em] font-semibold" style={{ fontSize: 8.5, color: "var(--text-3)" }}>
              {t("comp.art.accountEquity")}
            </span>
          </div>
          <div className="font-display font-bold tabular-nums" style={{ fontSize: 26, color: "var(--text-1)", letterSpacing: "-0.02em" }}>
            $50,128.40
          </div>
          <motion.div
            className="mt-1.5 inline-flex items-center gap-1 font-mono font-semibold rounded-full px-2 py-0.5"
            style={{
              fontSize: 9.5,
              color: "#54d97c",
              background: "color-mix(in srgb, #54d97c 12%, transparent)",
            }}
            animate={{ opacity: [1, 0.7, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <TrendingUp size={10} /> +12.4% · {t("comp.art.thisMonth")}
          </motion.div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[12px] font-mono tabular-nums">
            <div>
              <div className="text-text-3">{t("comp.art.margin")}</div>
              <div className="text-text-1 font-bold mt-0.5">$5,212.00</div>
            </div>
            <div>
              <div className="text-text-3">{t("comp.art.freeMargin")}</div>
              <div className="text-text-1 font-bold mt-0.5">$44,916.40</div>
            </div>
          </div>
        </motion.div>

        {/* CARD C · POSITION TOAST · front, smallest */}
        <motion.div
          className="absolute rounded-xl p-3 flex items-center gap-2.5"
          style={{
            ...glassCard("#54d97c", 96),
            left: "10%", bottom: "8%", width: "52%",
            transform: "rotateY(-4deg) rotateX(2deg) translateZ(80px)",
          }}
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
        >
          <div className="w-8 h-8 rounded-full grid place-items-center shrink-0"
               style={{ background: "color-mix(in srgb, #54d97c 16%, transparent)", border: "1px solid color-mix(in srgb, #54d97c 50%, transparent)" }}>
            <Check size={14} style={{ color: "#54d97c" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono font-bold uppercase tracking-wide" style={{ fontSize: 9, color: "var(--text-1)" }}>
              {t("comp.art.orderFilled")} · EUR/USD
            </div>
            <div className="font-mono tabular-nums mt-0.5" style={{ fontSize: 9, color: "var(--text-3)" }}>
              <span style={{ color: "#54d97c" }}>BUY 1.50</span> @ 1.0842 ·
              <motion.span className="ml-1 font-semibold" style={{ color: "#54d97c" }} animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.6, repeat: Infinity }}>
                +$128.40
              </motion.span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default InstitutionalArt;
